import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQuery, useQueryClient } from "react-query";
import { Snackbar, Alert, Box, Button } from "@mui/material";
import {
  NOTIFICATION_TYPES,
  isNotificationType,
  DEFAULT_DEDUPE_TTL_MS,
} from "../notifications/types";
import {
  buildDefaultNotificationPrefs,
  normalizePrefsPartial,
  parseNotificationPrefsFromApi,
} from "../notifications/prefsUtils";
import {
  NOTIFICATION_PREFS_KEY,
  NOTIFICATION_INBOX_LOCAL_KEY,
  NOTIFICATION_BANNER_DISMISS_KEY,
} from "../notifications/storageKeys";
import { fetchData } from "../api/client";
import {
  unlockNotificationAudio,
  isNotificationAudioUnlocked,
  playSoftMessageBeep,
  playMp3Url,
  getRequestAlertSoundUrl,
} from "../notifications/soundUtils";
import {
  appendOperatorNotification,
  fetchOperatorNotifications,
  getRequestDetails,
  markOperatorNotificationsRead,
} from "../api/client";

const BATCH_MS = 3200;
const SOUND_COOLDOWN_MS = 2800;
const MAX_INBOX_LOCAL = 80;
const MAX_TOASTS = 4;
const REQUEST_META_TTL_MS = 10 * 60 * 1000;

function loadPrefs() {
  try {
    const raw = localStorage.getItem(NOTIFICATION_PREFS_KEY);
    if (!raw) return buildDefaultNotificationPrefs();
    const p = JSON.parse(raw);
    return normalizePrefsPartial(p);
  } catch {
    return buildDefaultNotificationPrefs();
  }
}

function unwrapMePayload(d) {
  return d && typeof d === "object" && "data" in d ? d.data : d;
}

function savePrefs(prefs) {
  try {
    localStorage.setItem(NOTIFICATION_PREFS_KEY, JSON.stringify(prefs));
  } catch {
    /* ignore */
  }
}

function loadLocalInbox() {
  try {
    const raw = localStorage.getItem(NOTIFICATION_INBOX_LOCAL_KEY);
    const arr = raw ? JSON.parse(raw) : [];
    return Array.isArray(arr) ? arr : [];
  } catch {
    return [];
  }
}

function saveLocalInbox(items) {
  try {
    localStorage.setItem(
      NOTIFICATION_INBOX_LOCAL_KEY,
      JSON.stringify(items.slice(0, MAX_INBOX_LOCAL))
    );
  } catch {
    /* ignore */
  }
}

function parseConversationIdFromDeepLink(deepLink) {
  if (!deepLink || typeof deepLink !== "string") return null;
  const m = deepLink.match(/\/chat\/(\d+)/);
  return m ? String(m[1]) : null;
}

function absoluteUrlForWindowOpen(link) {
  if (!link || typeof link !== "string") return "";
  try {
    return new URL(link, window.location.origin).href;
  } catch {
    return link;
  }
}

const NotificationContext = createContext(null);

export function useNotifications() {
  const ctx = useContext(NotificationContext);
  if (!ctx) {
    throw new Error("useNotifications must be used within NotificationProvider");
  }
  return ctx;
}

/** Para hooks compartilhados (ex.: useChatState) quando o provider pode não existir em testes. */
export function useNotificationsOptional() {
  return useContext(NotificationContext);
}

function severityForType(type) {
  switch (type) {
    case NOTIFICATION_TYPES.security:
      return "error";
    case NOTIFICATION_TYPES.billing:
      return "warning";
    case NOTIFICATION_TYPES.new_request:
    case NOTIFICATION_TYPES.request_assigned:
      return "info";
    default:
      return "success";
  }
}

export function NotificationProvider({ children }) {
  const navigate = useNavigate();
  const { t } = useTranslation("notifications");
  const queryClient = useQueryClient();

  const [prefs, setPrefsState] = useState(loadPrefs);
  const [inbox, setInbox] = useState(loadLocalInbox);
  const [toasts, setToasts] = useState([]);
  const [bannerOpen, setBannerOpen] = useState(false);
  /** Indicador persistente (ex.: cor do sino) até salvar com sucesso. */
  const [prefsSyncFailed, setPrefsSyncFailed] = useState(false);
  /** Snackbar pode fechar por tempo sem esconder o estado de falha no sino. */
  const [prefsSyncSnackOpen, setPrefsSyncSnackOpen] = useState(false);

  const dedupeRef = useRef(new Map());
  const lastSoundAtRef = useRef({});
  const batchRef = useRef({ timer: null, pending: new Map() });
  const inboxRef = useRef(inbox);
  const requestMetaRef = useRef(new Map()); // conversationId -> { data, fetchedAt }
  const requestMetaInFlightRef = useRef(new Map()); // conversationId -> Promise

  const bearer =
    typeof window !== "undefined" ? window.localStorage.getItem("bearerToken") : null;
  const { data: meRes } = useQuery(
    ["me"],
    () => fetchData("/users/me"),
    {
      staleTime: 10_000,
      refetchOnWindowFocus: false,
      enabled: Boolean(bearer),
    }
  );

  const didHydrateServerPrefsRef = useRef(false);
  const userEditedPrefsRef = useRef(false);
  const persistPrefsTimerRef = useRef(null);
  const prefsRef = useRef(prefs);
  prefsRef.current = prefs;

  useEffect(() => {
    inboxRef.current = inbox;
  }, [inbox]);

  const runPersistPrefs = useCallback(
    async (next) => {
      try {
        await fetchData("/users/me", "PUT", { notification_prefs_json: next });
        queryClient.invalidateQueries(["me"]);
        setPrefsSyncFailed(false);
        setPrefsSyncSnackOpen(false);
      } catch {
        setPrefsSyncFailed(true);
        setPrefsSyncSnackOpen(true);
      }
    },
    [queryClient]
  );

  const dismissPrefsSyncError = useCallback(() => {
    setPrefsSyncFailed(false);
    setPrefsSyncSnackOpen(false);
  }, []);

  const retryPrefsSync = useCallback(() => {
    void runPersistPrefs(prefsRef.current);
  }, [runPersistPrefs]);

  const flushPersistPrefsNow = useCallback(async () => {
    if (!persistPrefsTimerRef.current) return;
    clearTimeout(persistPrefsTimerRef.current);
    persistPrefsTimerRef.current = null;
    if (typeof window === "undefined" || !window.localStorage.getItem("bearerToken")) return;
    await runPersistPrefs(prefsRef.current);
  }, [runPersistPrefs]);

  useEffect(() => {
    return () => {
      if (persistPrefsTimerRef.current) {
        clearTimeout(persistPrefsTimerRef.current);
        persistPrefsTimerRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    const onHidden = () => {
      if (document.visibilityState === "hidden") void flushPersistPrefsNow();
    };
    window.addEventListener("pagehide", flushPersistPrefsNow);
    document.addEventListener("visibilitychange", onHidden);
    return () => {
      window.removeEventListener("pagehide", flushPersistPrefsNow);
      document.removeEventListener("visibilitychange", onHidden);
    };
  }, [flushPersistPrefsNow]);

  const schedulePersistPrefs = useCallback(
    (next) => {
      if (typeof window === "undefined" || !window.localStorage.getItem("bearerToken")) return;
      if (persistPrefsTimerRef.current) clearTimeout(persistPrefsTimerRef.current);
      persistPrefsTimerRef.current = window.setTimeout(async () => {
        persistPrefsTimerRef.current = null;
        await runPersistPrefs(next);
      }, 700);
    },
    [runPersistPrefs]
  );

  useEffect(() => {
    if (!bearer) {
      didHydrateServerPrefsRef.current = false;
      userEditedPrefsRef.current = false;
      setPrefsSyncFailed(false);
      setPrefsSyncSnackOpen(false);
      return;
    }
    if (didHydrateServerPrefsRef.current) return;
    if (userEditedPrefsRef.current) {
      didHydrateServerPrefsRef.current = true;
      return;
    }
    const wrap = meRes || {};
    const inner = unwrapMePayload(wrap.data);
    const user = inner && typeof inner === "object" && inner.id ? inner : null;
    if (!user) return;
    didHydrateServerPrefsRef.current = true;
    const raw = user.notification_prefs_json;
    if (raw == null || raw === "") return;
    const parsed = parseNotificationPrefsFromApi(raw);
    if (!parsed) return;
    setPrefsState(parsed);
    savePrefs(parsed);
  }, [bearer, meRes]);

  const setPrefs = useCallback(
    (updater) => {
      userEditedPrefsRef.current = true;
      setPrefsState((prev) => {
        const next = typeof updater === "function" ? updater(prev) : updater;
        savePrefs(next);
        schedulePersistPrefs(next);
        return next;
      });
    },
    [schedulePersistPrefs]
  );

  const upsertMetaIntoInbox = useCallback((conversationId, meta) => {
    if (!conversationId || !meta) return;
    setInbox((prev) => {
      const next = prev.map((x) => {
        const cid = x.conversationId || parseConversationIdFromDeepLink(x.deepLink);
        if (String(cid || "") !== String(conversationId)) return x;
        if (x.requestMeta && x.requestMeta.protocol === meta.protocol) return x;
        return { ...x, conversationId: String(conversationId), requestMeta: meta };
      });
      saveLocalInbox(next);
      return next;
    });
  }, []);

  const ensureRequestMeta = useCallback(
    async (conversationId) => {
      const cid = String(conversationId || "").trim();
      if (!cid) return null;
      const cached = requestMetaRef.current.get(cid);
      if (cached && Date.now() - cached.fetchedAt < REQUEST_META_TTL_MS) {
        return cached.data;
      }
      if (requestMetaInFlightRef.current.has(cid)) {
        return requestMetaInFlightRef.current.get(cid);
      }
      const p = (async () => {
        try {
          const d = await getRequestDetails(cid);
          const meta = d
            ? {
                protocol: String(d.id || cid),
                clientName: d.cliente_nome ? String(d.cliente_nome) : "",
                typeName: d.tipo ? String(d.tipo) : "",
                subtype1: d.subtipo1 ? String(d.subtipo1) : "",
                subtype2: d.subtipo2 ? String(d.subtipo2) : "",
                subtype3: d.subtipo3 ? String(d.subtipo3) : "",
                originName: d.origem ? String(d.origem) : "",
              }
            : null;
          if (meta) {
            requestMetaRef.current.set(cid, { data: meta, fetchedAt: Date.now() });
            upsertMetaIntoInbox(cid, meta);
          }
          return meta;
        } catch {
          return null;
        } finally {
          requestMetaInFlightRef.current.delete(cid);
        }
      })();
      requestMetaInFlightRef.current.set(cid, p);
      return p;
    },
    [upsertMetaIntoInbox]
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const rows = await fetchOperatorNotifications(50);
        if (cancelled || !Array.isArray(rows) || rows.length === 0) return;
        setInbox((prev) => {
          const byId = new Map(prev.map((x) => [x.id, x]));
          for (const r of rows) {
            const id = `srv-${r.id}`;
            if (!byId.has(id)) {
              const cid = parseConversationIdFromDeepLink(r.deep_link);
              const evType = isNotificationType(r.event_type)
                ? r.event_type
                : NOTIFICATION_TYPES.system;
              byId.set(id, {
                id,
                serverId: r.id,
                type: evType,
                title: r.title || "",
                body: r.body || "",
                deepLink: r.deep_link || null,
                conversationId: cid,
                read: Boolean(r.read_at),
                createdAt: r.created_at || new Date().toISOString(),
                openLinkInNewTab: evType === NOTIFICATION_TYPES.legacy_socket,
              });
            }
          }
          const merged = Array.from(byId.values()).sort(
            (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
          );
          saveLocalInbox(merged);
          return merged.slice(0, MAX_INBOX_LOCAL);
        });
      } catch {
        /* offline / rota ainda indisponível */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    // tenta enriquecer metadados das solicitações visíveis (sem travar a UI)
    const ids = Array.from(
      new Set(
        inbox
          .map((x) => x.conversationId || parseConversationIdFromDeepLink(x.deepLink))
          .filter(Boolean)
          .map(String)
      )
    ).slice(0, 12);
    ids.forEach((cid) => {
      void ensureRequestMeta(cid);
    });
  }, [ensureRequestMeta, inbox]);

  useEffect(() => {
    try {
      if (localStorage.getItem(NOTIFICATION_BANNER_DISMISS_KEY) === "1") return;
      if (Notification.permission === "granted") return;
      setBannerOpen(true);
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    const onInteract = () => unlockNotificationAudio();
    window.addEventListener("click", onInteract, { passive: true });
    window.addEventListener("keydown", onInteract, { passive: true });
    return () => {
      window.removeEventListener("click", onInteract);
      window.removeEventListener("keydown", onInteract);
    };
  }, []);

  const shouldPlaySound = useCallback(
    (type, isConversationFocused) => {
      if (!prefs.soundEnabled) return false;
      if (prefs.soundOnlyBackground) {
        if (document.visibilityState === "visible" && isConversationFocused) return false;
      }
      const now = Date.now();
      const last = lastSoundAtRef.current[type] || 0;
      if (now - last < SOUND_COOLDOWN_MS) return false;
      lastSoundAtRef.current[type] = now;
      return true;
    },
    [prefs.soundEnabled, prefs.soundOnlyBackground]
  );

  const playSoundForType = useCallback((type) => {
    if (!isNotificationAudioUnlocked()) return;
    if (
      type === NOTIFICATION_TYPES.new_request ||
      type === NOTIFICATION_TYPES.request_assigned
    ) {
      playMp3Url(getRequestAlertSoundUrl(), 0.45);
      return;
    }
    if (type === NOTIFICATION_TYPES.chat_message) {
      playSoftMessageBeep();
      return;
    }
    playSoftMessageBeep();
  }, []);

  const pushToast = useCallback((item) => {
    const id = item.id || `toast-${Date.now()}`;
    setToasts((prev) => {
      const next = [...prev, { ...item, id, open: true }];
      return next.slice(-MAX_TOASTS);
    });
    window.setTimeout(() => {
      setToasts((prev) => prev.map((x) => (x.id === id ? { ...x, open: false } : x)));
    }, 5200);
  }, []);

  const dispatchRaw = useCallback(
    (payload) => {
      const {
        type: rawType,
        title,
        body = "",
        conversationId,
        deepLink,
        dedupeKey,
        skipSound,
        skipDesktop,
        openLinkInNewTab = false,
      } = payload;

      const type = isNotificationType(rawType) ? rawType : NOTIFICATION_TYPES.system;
      if (prefs.categories[type] === false) return;

      const key = dedupeKey || `${type}-${conversationId || ""}-${title}`;
      const now = Date.now();
      const prevT = dedupeRef.current.get(key);
      if (prevT && now - prevT < DEFAULT_DEDUPE_TTL_MS) return;
      dedupeRef.current.set(key, now);

      const isConvFocused =
        Boolean(conversationId) &&
        typeof window !== "undefined" &&
        window.location.pathname.includes(`/chat/${conversationId}`);

      const id = `local-${now}-${Math.random().toString(36).slice(2, 9)}`;
      const link =
        deepLink ||
        (conversationId ? `/chat/${conversationId}` : null);

      const cid = conversationId != null ? String(conversationId) : parseConversationIdFromDeepLink(link);
      const cachedMeta = cid ? requestMetaRef.current.get(String(cid))?.data : null;
      const entry = {
        id,
        type,
        title,
        body,
        deepLink: link,
        conversationId: cid,
        requestMeta: cachedMeta || null,
        read: false,
        createdAt: new Date().toISOString(),
        openLinkInNewTab: Boolean(openLinkInNewTab),
      };

      setInbox((prev) => {
        const next = [entry, ...prev].slice(0, MAX_INBOX_LOCAL);
        saveLocalInbox(next);
        return next;
      });

      pushToast({
        id,
        severity: severityForType(type),
        title,
        message: body,
        deepLink: link,
        openLinkInNewTab: Boolean(openLinkInNewTab),
      });

      if (cid) {
        void ensureRequestMeta(cid);
      }

      if (!skipSound && shouldPlaySound(type, isConvFocused)) {
        playSoundForType(type);
      }

      const wantDesktop =
        !skipDesktop &&
        prefs.desktopEnabled &&
        Notification.permission === "granted" &&
        !(document.visibilityState === "visible" && isConvFocused);

      if (wantDesktop && "Notification" in window) {
        try {
          const n = new Notification(title, {
            body: body || undefined,
            tag: key,
          });
          n.onclick = () => {
            window.focus();
            if (link) {
              if (openLinkInNewTab) {
                const url = absoluteUrlForWindowOpen(link);
                if (url) window.open(url, "_blank", "noopener,noreferrer");
              } else {
                navigate(link);
              }
            }
            n.close();
          };
        } catch {
          /* ignore */
        }
      }

      appendOperatorNotification({
        event_type: type,
        title,
        body,
        deep_link: link,
      }).catch(() => {});
    },
    [ensureRequestMeta, navigate, playSoundForType, prefs, pushToast, shouldPlaySound]
  );

  const lastMetaRef = useRef(new Map());

  const flushBatch = useCallback(() => {
    const { pending } = batchRef.current;
    batchRef.current.timer = null;
    if (!pending.size) return;

    const convIds = [...pending.keys()];
    pending.clear();

    if (convIds.length === 1) {
      const cid = convIds[0];
      const meta = lastMetaRef.current.get(cid) || {};
      lastMetaRef.current.delete(cid);
      if (meta.isConversationSelected && document.visibilityState === "visible") {
        return;
      }

      const ev = meta.eventName || "message";
      const type =
        ev === "request" ? NOTIFICATION_TYPES.new_request : NOTIFICATION_TYPES.chat_message;
      dispatchRaw({
        type,
        title:
          type === NOTIFICATION_TYPES.new_request
            ? t("titles.newRequest")
            : t("titles.newMessage"),
        body: t("bodies.singleConversation", { id: cid }),
        conversationId: cid,
        dedupeKey: `batch-${type}-${cid}`,
      });
      return;
    }

    convIds.forEach((id) => lastMetaRef.current.delete(id));

    dispatchRaw({
      type: NOTIFICATION_TYPES.chat_message,
      title: t("titles.batchTitle"),
      body: t("bodies.multiConversations", { count: convIds.length }),
      dedupeKey: `batch-multi-${convIds.sort().join(",")}-${Date.now()}`,
    });
  }, [dispatchRaw, t]);

  const queueSocketActivity = useCallback(
    ({ eventName, conversationId, isConversationSelected }) => {
      if (eventName === "messagesReload") return;
      const cid = String(conversationId);
      if (!cid || cid === "undefined" || cid === "null") return;

      const pending = batchRef.current.pending;
      pending.set(cid, true);
      lastMetaRef.current.set(cid, {
        eventName,
        isConversationSelected: Boolean(isConversationSelected),
      });

      if (batchRef.current.timer) {
        clearTimeout(batchRef.current.timer);
      }
      batchRef.current.timer = window.setTimeout(flushBatch, BATCH_MS);
    },
    [flushBatch]
  );

  const dispatchFromSocketLegacy = useCallback(
    (payload) => {
      const mensagem =
        typeof payload?.mensagem === "string"
          ? payload.mensagem
          : payload?.body || "";
      const link = payload?.link || payload?.deepLink || null;
      dispatchRaw({
        type: NOTIFICATION_TYPES.legacy_socket,
        title: `${t("titles.legacy")}${t("titles.legacyXpro2Suffix")}`,
        body: mensagem.slice(0, 280),
        deepLink: link,
        dedupeKey: `legacy-${link || ""}-${mensagem.slice(0, 40)}`,
        openLinkInNewTab: true,
      });
    },
    [dispatchRaw, t]
  );

  const requestDesktopPermission = useCallback(async () => {
    if (!("Notification" in window)) return "unsupported";
    const r = await Notification.requestPermission();
    if (r === "granted") {
      setPrefs((p) => ({ ...p, desktopEnabled: true }));
    }
    return r;
  }, [setPrefs]);

  const markRead = useCallback(async (ids) => {
    const serverIds = ids
      .map((id) => {
        const m = String(id).match(/^srv-(\d+)$/);
        return m ? Number(m[1]) : null;
      })
      .filter((x) => x != null);

    setInbox((prev) => {
      const next = prev.map((x) => (ids.includes(x.id) ? { ...x, read: true } : x));
      saveLocalInbox(next);
      return next;
    });

    if (serverIds.length) {
      try {
        await markOperatorNotificationsRead(serverIds);
      } catch {
        /* ignore */
      }
    }
  }, []);

  const markAllRead = useCallback(async () => {
    let serverIds = [];
    setInbox((prev) => {
      serverIds = prev.map((x) => x.serverId).filter((x) => x != null);
      const next = prev.map((x) => ({ ...x, read: true }));
      saveLocalInbox(next);
      return next;
    });
    if (serverIds.length) {
      try {
        await markOperatorNotificationsRead(serverIds);
      } catch {
        /* ignore */
      }
    }
  }, []);

  const unreadCount = useMemo(
    () => inbox.filter((x) => !x.read).length,
    [inbox]
  );

  const dismissBanner = useCallback(() => {
    try {
      localStorage.setItem(NOTIFICATION_BANNER_DISMISS_KEY, "1");
    } catch {
      /* ignore */
    }
    setBannerOpen(false);
  }, []);

  const value = useMemo(
    () => ({
      prefs,
      setPrefs,
      inbox,
      unreadCount,
      dispatch: dispatchRaw,
      dispatchRealtimeSocket: queueSocketActivity,
      dispatchFromSocketLegacy,
      markRead,
      markAllRead,
      requestDesktopPermission,
      bannerOpen,
      dismissBanner,
      prefsSyncFailed,
      dismissPrefsSyncError,
      retryPrefsSync,
    }),
    [
      prefs,
      setPrefs,
      inbox,
      unreadCount,
      dispatchRaw,
      queueSocketActivity,
      dispatchFromSocketLegacy,
      markRead,
      markAllRead,
      requestDesktopPermission,
      bannerOpen,
      dismissBanner,
      prefsSyncFailed,
      dismissPrefsSyncError,
      retryPrefsSync,
    ]
  );

  return (
    <NotificationContext.Provider value={value}>
      {children}
      <Snackbar
        open={prefsSyncSnackOpen}
        autoHideDuration={8000}
        onClose={(_, reason) => {
          if (reason === "clickaway" || reason === "timeout") setPrefsSyncSnackOpen(false);
        }}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        sx={{ bottom: { xs: 96, sm: 24 } }}
      >
        <Alert
          severity="warning"
          variant="filled"
          onClose={() => setPrefsSyncSnackOpen(false)}
          action={
            <Button color="inherit" size="small" sx={{ fontWeight: 700 }} onClick={retryPrefsSync}>
              {t("prefsSync.retry")}
            </Button>
          }
          sx={{ alignItems: "center" }}
        >
          {t("prefsSync.failed")}
        </Alert>
      </Snackbar>
      <Box
        sx={{
          position: "fixed",
          bottom: 16,
          right: 16,
          zIndex: (theme) => theme.zIndex.snackbar + 2,
          display: "flex",
          flexDirection: "column",
          gap: 1,
          maxWidth: 380,
          pointerEvents: "none",
          "& > *": { pointerEvents: "auto" },
        }}
      >
        {toasts.map((toast) => (
          <Snackbar
            key={toast.id}
            open={toast.open}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            sx={{ position: "relative", bottom: "auto", right: "auto" }}
          >
            <Alert
              severity={toast.severity || "info"}
              variant="filled"
              onClose={() =>
                setToasts((prev) => prev.filter((x) => x.id !== toast.id))
              }
              onClick={() => {
                if (toast.deepLink) {
                  if (toast.openLinkInNewTab) {
                    const url = absoluteUrlForWindowOpen(toast.deepLink);
                    if (url) window.open(url, "_blank", "noopener,noreferrer");
                  } else {
                    navigate(toast.deepLink);
                  }
                }
                setToasts((prev) => prev.filter((x) => x.id !== toast.id));
              }}
              sx={{ cursor: toast.deepLink ? "pointer" : "default", width: "100%" }}
            >
              <Box component="span" sx={{ fontWeight: 700, display: "block" }}>
                {toast.title}
              </Box>
              {toast.message ? (
                <Box component="span" sx={{ fontSize: "0.875rem", opacity: 0.95 }}>
                  {toast.message}
                </Box>
              ) : null}
            </Alert>
          </Snackbar>
        ))}
      </Box>

      <Snackbar
        open={bannerOpen}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        sx={{ mt: 8 }}
      >
        <Alert
          severity="info"
          onClose={dismissBanner}
          action={
            <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
              <Box
                component="button"
                type="button"
                onClick={async () => {
                  await requestDesktopPermission();
                  dismissBanner();
                }}
                sx={{
                  border: "none",
                  background: "rgba(255,255,255,0.2)",
                  color: "inherit",
                  padding: "4px 10px",
                  borderRadius: 1,
                  cursor: "pointer",
                  fontWeight: 600,
                }}
              >
                {t("banner.enable")}
              </Box>
              <Box
                component="button"
                type="button"
                onClick={() => {
                  navigate("/profile#profile-notifications");
                  dismissBanner();
                }}
                sx={{
                  border: "none",
                  background: "transparent",
                  color: "inherit",
                  textDecoration: "underline",
                  cursor: "pointer",
                }}
              >
                {t("banner.settings")}
              </Box>
            </Box>
          }
        >
          {t("banner.text")}
        </Alert>
      </Snackbar>
    </NotificationContext.Provider>
  );
}
